<script setup lang="ts">
import type { KjdsListPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsListPageProps>(), {})

console.log('article: kjdsPageConfig', props.kjdsPageConfig)

const app = useNuxtApp()
const route = useRoute()
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()

const baseTitle = '巡回展'
const headTitle = baseTitle

const pageData = {
  tags: [] as ArticleTag[],
  items: [] as Article[],
  meta: {} as ApiMeta,
}

const sortInfo = storeArticleList.getPageSortInfo(props.kjdsPageConfig)
const modelSort = ref(sortInfo?.defaultValue)
if (route.query.sort) {
  modelSort.value = route.query.sort as string
}

const articleName = ref(props.kjdsPageConfig.articleTypeSlug)

const setHead = () => {
  const headDescription = '日本最大級のミュージアム情報サイト、アイエム［インターネットミュージアム］の巡回展情報。巡回する展覧会の会場や会期のほか、取材レポートも紹介。巡回展を探すならアイエムで！'
  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: false,
      to: { path: '/event' },
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const initPage = async () => {
  const options: KjdsFetchListOptions = { query: { ...route.query } }
  if (route.query.term) {
    // 指定日の場合は日時範囲指定
    options.query = {
      ...options.query,
      dateBefore: useDayjs(route.query.term as string).format('YYYY/MM/DD 23:59:59'),
      dateAfter: useDayjs(route.query.term as string).format('YYYY/MM/DD 00:00:00'),
    }
  }

  // エリアクエリーがある場合はエリアIDを展開
  if (route.query.area) {
    const areaQuery = route.query.area as string
    const ary = areaQuery.split('-')
    if (ary.length === 2) {
      const val = Number(ary[1])
      if (ary[0] === 'area') {
        const areaId = val - 0
        const masterArea = storeIndex.state.master.prefecture_area.details
        masterArea.forEach((area) => {
          if (area.id === areaId) {
            options.query['prefecture'] = area.value
          }
        })
      } else if ((ary[0] === 'pref')) {
        options.query['prefecture'] = ary[1]
      }
    }
  }

  const [_resSite, _resMaster, resList, resTags] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchList(props.kjdsPageConfig.list.mainListName, options),
    storeArticleList.fetchTags(articleName.value),
  ])
  pageData.items = resList.data.articles as Article[]
  pageData.meta = resList.meta
  pageData.tags = resTags.data
}
await initPage()
setHead()

const onChangeSort = (val) => {
  modelSort.value = val
  const query = { ...route.query }
  if (val) {
    query.sort = val
  } else {
    delete query.sort
  }
  navigateTo({
    path: route.path,
    query,
  })
}
</script>

<template>
  <div class="p-articlePage">
    <h2 class="c-heading_page">
      {{ headTitle }}
    </h2>

    <SearchPanel
      v-bind:article-name="articleName"
      v-bind:categories="[]"
      v-bind:tags="pageData.tags"
      v-bind:query="route.query"
    />

    <div class="p-articlePage_result">
      <div
        class="c-listHeader"
      >
        <div class="c-listHeader_col">
          <div class="c-listHeader_col_contentSearch">
            全 {{ pageData.meta.count.toLocaleString() }}件
          </div>
        </div>
        <div class="c-listHeader_dropDown">
          <v-select
            v-model="modelSort"
            class="IMCustomOverflowButton marginLeft c-searchSort"
            v-bind:items="sortInfo.items"
            hide-details
            variant="outlined"
            density="comfortable"
            v-on:update:model-value="onChangeSort"
          />
        </div>
      </div>

      <template
        v-if="!pageData.items?.length"
      >
        <div class="p-articlePage_empty">
          お探しの情報は見つかりませんでした
        </div>
      </template>

      <div class="p-articlePage_list">
        <JyunkaiItem
          v-for="item in pageData.items"
          v-bind:key="item.id"
          v-bind:item="item"
          v-bind:show-label="true"
        />
      </div>

      <template
        v-if="pageData.items?.length"
      >
        <Pagination
          v-bind:page="pageData.meta.page_no"
          v-bind:length="pageData.meta.total_page"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-link {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;

  @media screen and (width <= 500px) {
    flex-wrap: wrap;
    justify-content: center;
  }

  &_btn {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 48%;
    height: 45px;
    margin: 0;
    transition: 300ms;
    border: 1px solid var(--g_color_table_border_gray);
    text-decoration: none;

    @media screen and (width <= 500px) {
      width: 100%;
    }

    &_first {
      @media screen and (width <= 500px) {
        margin-bottom: 12px;
      }
    }

    &:hover {
      opacity: 0.7;
    }

    &_text {
      color: var(--color_text_base);
      font-size: 15px;
      font-weight: bold;
      line-height: 2.286;
    }
  }
}
</style>
